import Tippy, { type TippyProps } from '@tippyjs/react/headless';
import { useCallback, useState, type MouseEvent } from 'react';

import { ButtonIcon } from '@/components/shared/buttons';
import { DropdownContext, type DropdownContextValue } from '@/contexts/shared';

import type { ContextMenuProps } from './ContextMenu';
import style from './ContextMenu.module.sass';

type PortaledContextMenuProps = Omit<ContextMenuProps, 'portal'>;
type RenderAttrs = Parameters<Required<TippyProps>['render']>[0];

export const PortaledContextMenu = ({
  children,
  ariaLabel,
  triggerClassName,
  triggerSize,
  triggerVariant,
  onToggle,
}: PortaledContextMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const setMenuIsOpen = useCallback((nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);
    onToggle?.(nextIsOpen);
  }, [onToggle]);

  const contextValue: DropdownContextValue = {
    isOpen,
    setIsOpen: setMenuIsOpen,
  };

  const renderMenu = useCallback((attrs: RenderAttrs) => (
    <div
      className={style.menu}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      {...attrs}>
      {children}
    </div>
  ), [children]);

  const toggleIsOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuIsOpen(!isOpen);
  };

  return (
    <DropdownContext.Provider value={contextValue}>
      <Tippy
        appendTo={() => document.body}
        duration={0}
        interactive={true}
        offset={[0, 8]}
        onClickOutside={() => setMenuIsOpen(false)}
        placement='bottom-end'
        render={renderMenu}
        trigger='manual'
        visible={isOpen}>
        <span className={style.triggerWrapper}>
          <ButtonIcon
            ariaLabel={ariaLabel}
            iconName='MoreVertical'
            isActive={isOpen}
            onClick={toggleIsOpen}
            className={triggerClassName}
            size={triggerSize}
            variant={triggerVariant}
          />
        </span>
      </Tippy>
    </DropdownContext.Provider>
  );
};
